export type BlogSection = {
  heading: string;
  paragraphs: readonly string[];
};

export type BlogFaq = {
  question: string;
  answer: string;
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  metaTitle: string;
  metaDescription: string;
  publishedAt: string;
  updatedAt: string;
  readingTime: string;
  heroImage: string;
  heroAlt: string;
  detailImage?: string;
  detailAlt?: string;
  sections: readonly BlogSection[];
  faqs: readonly BlogFaq[];
  relatedServicePaths: readonly string[];
};

/** Roofing guides migrated from revolingroup.com (same slugs, Revolin Roofing CTAs). */
export const blogPosts: readonly BlogPost[] = [
  {
    slug: "signs-roof-needs-repair-vancouver",
    title: "Signs Your Vancouver Roof Needs Repair or Replacement",
    excerpt:
      "Warning signs that deserve a professional roof assessment, from active leaks and damaged shingles to flashing failures, soft decking, ventilation concerns, and repeated patching.",
    metaTitle: "Signs Your Vancouver Roof Needs Repair or Replacement | Revolin Roofing",
    metaDescription:
      "Warning signs that deserve a professional roof assessment in Metro Vancouver, from leaks and damaged shingles to flashing failures and repeated patching.",
    publishedAt: "2026-09-16",
    updatedAt: "2026-09-16",
    readingTime: "4 min",
    heroImage: "/images/blog/signs-roof-needs-repair-vancouver-hero.webp",
    heroAlt: "Vancouver residential roof after rainfall with damaged asphalt shingles near flashing",
    detailImage: "/images/blog/signs-roof-needs-repair-vancouver-detail.webp",
    detailAlt: "Roof flashing around a vent penetration with subtle wear",
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/roof-health-check-maintenance",
      "/services/asphalt-shingle-replacement",
    ],
    sections: [
      {
        heading: "Quick answer",
        paragraphs: [
          "Water stains are an obvious warning sign, but roof problems can also show up as damaged or missing shingles, failed sealant, lifted flashing, granule loss, moss-related deterioration, soft decking, recurring leaks around penetrations, or interior moisture symptoms.",
        ],
      },
      {
        heading: "Leaks near penetrations",
        paragraphs: [
          "Skylights, chimneys, vents, walls, valleys, and roof edges are common places to investigate because multiple materials and water paths meet there.",
        ],
      },
      {
        heading: "Repeated repairs can signal a larger issue",
        paragraphs: [
          "A localized repair may be appropriate for an isolated defect. Recurrent failures across an aging assembly may justify comparing repair with broader replacement.",
        ],
      },
      {
        heading: "Check the attic when possible",
        paragraphs: [
          "Moisture staining, damp insulation, condensation, daylight through openings, or ventilation problems can provide clues, but roof access and diagnosis should be handled safely.",
        ],
      },
      {
        heading: "After severe weather",
        paragraphs: [
          "Wind and heavy rain can expose weak points. Photograph visible damage from the ground and avoid unsafe roof access.",
        ],
      },
      {
        heading: "What to prepare before requesting an assessment",
        paragraphs: [
          "To make an initial conversation more useful, prepare the property location, a few clear photos, approximate dimensions, the outcome you want, what you want to keep versus replace, preferred materials if known, and your ideal timing. That helps separate a broad idea from a buildable scope.",
        ],
      },
      {
        heading: "When a site visit makes sense",
        paragraphs: [
          "A site visit is especially useful when the project involves hidden conditions, water damage, uncertain measurements, difficult access, or a major reconfiguration. Revolin Roofing documents conditions on site and returns a written scope before recommending repair or replacement.",
        ],
      },
    ],
    faqs: [
      {
        question: "Does a ceiling stain always mean the roof is leaking?",
        answer:
          "No. Plumbing, condensation, envelope issues, or older repaired leaks can produce similar symptoms. The source should be diagnosed.",
      },
      {
        question: "Can one missing shingle be repaired?",
        answer:
          "Often localized damage can be repaired, but surrounding material condition, matching, underlayment, and the cause should be checked.",
      },
      {
        question: "Should I climb onto the roof to inspect it?",
        answer:
          "Avoid unsafe access. Many conditions can be documented from the ground or assessed by qualified professionals with proper safety equipment.",
      },
    ],
  },
  {
    slug: "asphalt-shingles-vs-metal-roofing-vancouver",
    title: "Asphalt Shingles vs Metal Roofing in Vancouver",
    excerpt:
      "Compare asphalt shingles and metal roofing for Vancouver homes, including roof geometry, underlayment, ventilation, flashing, maintenance, appearance, and lifecycle planning.",
    metaTitle: "Asphalt Shingles vs Metal Roofing in Vancouver | Revolin Roofing",
    metaDescription:
      "Compare asphalt shingles and metal roofing for Metro Vancouver homes: assembly design, ventilation, flashing, maintenance, and when each system fits.",
    publishedAt: "2026-09-16",
    updatedAt: "2026-09-16",
    readingTime: "4 min",
    heroImage: "/images/blog/asphalt-shingles-vs-metal-roofing-vancouver-hero.webp",
    heroAlt: "Vancouver home comparing asphalt shingle and standing-seam metal roof profiles",
    detailImage: "/images/blog/asphalt-shingles-vs-metal-roofing-vancouver-detail.webp",
    detailAlt: "Valley and wall flashing transition during roof installation",
    relatedServicePaths: [
      "/services/asphalt-shingle-replacement",
      "/services/metal-roof-replacement",
      "/roof-systems",
    ],
    sections: [
      {
        heading: "Quick answer",
        paragraphs: [
          "Asphalt shingles and metal roofing can both perform well when the complete roof assembly is properly designed and installed. Material choice should account for roof slope and geometry, existing deck condition, ventilation, underlayment, flashing, penetrations, drainage, architecture, budget, and maintenance expectations.",
        ],
      },
      {
        heading: "The roof is a system",
        paragraphs: [
          "Leaks frequently occur at transitions and penetrations rather than in the middle of a simple field. Valleys, chimneys, skylights, wall interfaces, vents, edges, gutters, and flashing details deserve careful attention.",
        ],
      },
      {
        heading: "Replacement is a chance to inspect what is hidden",
        paragraphs: [
          "Removing old roofing allows the deck, previous repairs, ventilation paths, flashings, and water-damaged areas to be assessed.",
        ],
      },
      {
        heading: "Appearance changes the house",
        paragraphs: [
          "Metal profile, seam layout, colour, shingle exposure, edge metal, gutters, and roof complexity all affect curb appeal.",
        ],
      },
      {
        heading: "Choose installers for detailing, not just product",
        paragraphs: [
          "A premium material cannot compensate for poor transitions, fastener practices, flashing, or site protection.",
        ],
      },
      {
        heading: "What to prepare before requesting an assessment",
        paragraphs: [
          "Share the property location, photos, approximate dimensions, goals, and timing. Revolin Roofing uses that context to plan a useful on-site review.",
        ],
      },
      {
        heading: "When a site visit makes sense",
        paragraphs: [
          "System changes, uncertain deck condition, or mixed-slope geometry usually require seeing the roof before a written scope and price are issued.",
        ],
      },
    ],
    faqs: [
      {
        question: "Is metal roofing noisy in rain?",
        answer:
          "Perceived sound depends on the roof assembly, insulation, attic, deck, profile, and interior conditions—not just the exterior material.",
      },
      {
        question: "Can metal roofing go over shingles?",
        answer:
          "Some systems and jurisdictions may allow certain approaches, but existing condition, assembly design, manufacturer requirements, and applicable rules need review.",
      },
      {
        question: "Which is better for Vancouver rain?",
        answer:
          "Both can perform well. Water-shedding design, underlayment, flashings, penetrations, drainage, slope, and workmanship are critical.",
      },
    ],
  },
  {
    slug: "ceiling-water-stain-roof-or-plumbing",
    title: "Ceiling Water Stains: Roof Leak or Plumbing Leak?",
    excerpt:
      "Use timing, location, fixtures, weather, attic clues, and moisture patterns to help narrow the source of a ceiling stain before repair.",
    metaTitle: "Ceiling Water Stains: Roof Leak or Plumbing? | Revolin Roofing",
    metaDescription:
      "Narrow the source of a ceiling water stain using timing, weather, fixtures, and attic clues before cosmetic repair.",
    publishedAt: "2026-09-16",
    updatedAt: "2026-09-16",
    readingTime: "4 min",
    heroImage: "/images/blog/ceiling-water-stain-roof-or-plumbing-hero.webp",
    heroAlt: "Interior ceiling with a subtle water stain in a Metro Vancouver home",
    detailImage: "/images/blog/ceiling-water-stain-roof-or-plumbing-detail.webp",
    detailAlt: "Attic roof area and plumbing as possible moisture sources",
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/flashing-roof-penetration-repair",
      "/services/roof-health-check-maintenance",
    ],
    sections: [
      {
        heading: "Quick answer",
        paragraphs: [
          "A ceiling stain can come from roofing, plumbing, condensation, exterior-envelope issues, or an old leak. The most useful clues are when it appears, what is above it, whether weather affects it, and whether nearby fixtures or mechanical systems were used.",
        ],
      },
      {
        heading: "Weather-related patterns",
        paragraphs: [
          "A stain that grows after wind-driven rain may point toward roof or exterior water entry, but water can travel before it becomes visible.",
        ],
      },
      {
        heading: "Fixture-related patterns",
        paragraphs: [
          "Leaks below bathrooms, kitchens, laundry, or mechanical equipment may appear only during use. Supply, drain, overflow, seal, and condensation issues can each behave differently.",
        ],
      },
      {
        heading: "Do not repaint first",
        paragraphs: [
          "A stain blocker can hide the symptom while moisture continues. Confirm the source and dry or repair affected materials before cosmetic work.",
        ],
      },
      {
        heading: "Document the timing",
        paragraphs: [
          "Photos with dates, weather conditions, and fixture use can help diagnose an intermittent problem during an assessment.",
        ],
      },
      {
        heading: "What to prepare before requesting an assessment",
        paragraphs: [
          "Note when the stain appeared, what is directly above it, recent weather, and any fixture use. Photos from multiple angles help the first visit focus on the right assembly.",
        ],
      },
      {
        heading: "When a site visit makes sense",
        paragraphs: [
          "When the source is unclear or the stain returns after drying, an on-site review of the roof, attic, and adjacent systems is the practical next step.",
        ],
      },
    ],
    faqs: [
      {
        question: "Can water travel far from the leak?",
        answer:
          "Yes. It can move along framing, pipes, membranes, and ceiling surfaces before showing.",
      },
      {
        question: "Is a dry stain still a problem?",
        answer:
          "It may be from an old resolved leak, but that should be confirmed rather than assumed.",
      },
      {
        question: "Who should I call first?",
        answer:
          "That depends on what is above the stain and when it occurs. Revolin Roofing can help when roofing or attic evidence is involved; plumbing may need a plumber when fixtures are the trigger.",
      },
    ],
  },
];

export const blogPostsBySlug = Object.fromEntries(blogPosts.map((post) => [post.slug, post])) as Record<
  string,
  BlogPost
>;

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPostsBySlug[slug];
}
